'use client';

/**
 * RelatedTasksFilterPills Component
 *
 * Display active filters as removable pills for Step4RelatedTasks.
 * Mirrors HistoryFilterPills pattern for consistency.
 *
 * Features:
 * - Assignee pill (if selected)
 * - Location pill (if non-empty, truncated at 20 chars)
 * - Date range pill (combined from/to)
 * - Individual pill removal
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/components
 */

import { memo, useMemo } from 'react';
import { Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocalizedDate } from '../../../../../../hooks/useLocalizedDate';
import { FilterPill } from '../../../../../ui/FilterPill';

interface RelatedTasksFilterPillsProps {
  assignee: string | null;
  location: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  onRemoveAssignee: () => void;
  onRemoveLocation: () => void;
  onRemoveDateRange: () => void;
}

export const RelatedTasksFilterPills = memo(function RelatedTasksFilterPills({
  assignee,
  location,
  dateFrom,
  dateTo,
  onRemoveAssignee,
  onRemoveLocation,
  onRemoveDateRange
}: RelatedTasksFilterPillsProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  // Build pills array
  const pills = useMemo(() => {
    const result: Array<{
      id: string;
      label: string;
      value: string;
      onRemove: () => void;
    }> = [];

    // Assignee pill
    if (assignee) {
      result.push({
        id: 'assignee',
        label: t('wizard.step4.filters.assigneeLabel'),
        value: assignee,
        onRemove: onRemoveAssignee
      });
    }

    // Location pill
    if (location) {
      const truncatedLocation = location.length > 20
        ? `${location.substring(0, 20)}...`
        : location;

      result.push({
        id: 'location',
        label: t('wizard.step4.filters.locationLabel'),
        value: truncatedLocation,
        onRemove: onRemoveLocation
      });
    }

    // Date Range pill (combined)
    if (dateFrom || dateTo) {
      const fromText = dateFrom ? formatDateCompact(dateFrom) : t('filters.none');
      const toText = dateTo ? formatDateCompact(dateTo) : t('filters.none');

      result.push({
        id: 'date-range',
        label: t('wizard.step4.filters.dateRangeLabel'),
        value: `${fromText} - ${toText}`,
        onRemove: onRemoveDateRange
      });
    }

    return result;
  }, [assignee, location, dateFrom, dateTo, t, formatDateCompact, onRemoveAssignee, onRemoveLocation, onRemoveDateRange]);

  // Don't render if no active filters
  if (pills.length === 0) {
    return null;
  }

  return (
    <Group gap="xs" wrap="wrap">
      {pills.map((pill) => (
        <FilterPill
          key={pill.id}
          label={pill.label}
          value={pill.value}
          onRemove={pill.onRemove}
          ariaLabel={t('filters.clearFilter')}
        />
      ))}
    </Group>
  );
});
